import { MapPin, Phone, Clock, Mail } from "lucide-react";
import { STORE } from "../lib/api";

export default function Contact() {
    return (
        <main data-testid="contact-page">
            <section className="bg-[#0033A0] py-14 md:py-20" data-testid="contact-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white" data-testid="contact-heading">
                        Contact Us
                    </h1>
                    <p className="mt-3 text-blue-100 max-w-2xl" data-testid="contact-subtitle">
                        Visit us in store, give us a call, or get directions — we're open seven days a week.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24 bg-slate-50" data-testid="contact-body">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        {/* Address card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-testid="contact-address-card">
                            <div className="flex items-start gap-4">
                                <span className="rounded-full bg-[#0033A0] p-3 shrink-0">
                                    <MapPin className="h-6 w-6 text-[#40E0D0]" />
                                </span>
                                <div>
                                    <h2 className="font-heading font-bold text-xl text-slate-900">Our Address</h2>
                                    <address className="not-italic mt-2 text-slate-600 leading-relaxed" data-testid="contact-address">
                                        {STORE.address}
                                    </address>
                                    <a
                                        href={STORE.directionsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-directions-button"
                                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0033A0] px-6 py-2.5 font-heading font-bold text-white text-sm transition-colors hover:bg-[#002277]"
                                    >
                                        <MapPin className="h-4 w-4" />
                                        Get Directions
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Phones card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-testid="contact-phones-card">
                            <div className="flex items-start gap-4">
                                <span className="rounded-full bg-[#0033A0] p-3 shrink-0">
                                    <Phone className="h-6 w-6 text-[#40E0D0]" />
                                </span>
                                <div>
                                    <h2 className="font-heading font-bold text-xl text-slate-900">Call Us</h2>
                                    <ul className="mt-2 space-y-2">
                                        {STORE.phones.map((p) => (
                                            <li key={p.tel}>
                                                <a
                                                    href={`tel:${p.tel}`}
                                                    data-testid={`contact-phone-${p.tel.slice(-4)}`}
                                                    className="text-lg font-bold text-[#0033A0] hover:underline underline-offset-4"
                                                >
                                                    {p.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* WhatsApp card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-testid="contact-whatsapp-card">
                            <div className="flex items-start gap-4">
                                <span className="rounded-full bg-[#25D366] p-3 shrink-0">
                                    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden="true">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                </span>
                                <div>
                                    <h2 className="font-heading font-bold text-xl text-slate-900">WhatsApp Us</h2>
                                    <a
                                        href={STORE.whatsapp.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-whatsapp"
                                        className="mt-2 inline-block text-lg font-bold text-[#0033A0] hover:underline underline-offset-4"
                                    >
                                        {STORE.whatsapp.label}
                                    </a>
                                    <p className="text-sm text-slate-500 mt-1">Message us anytime — we reply during opening hours.</p>
                                </div>
                            </div>
                        </div>

                        {/* Email card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-testid="contact-email-card">
                            <div className="flex items-start gap-4">
                                <span className="rounded-full bg-[#0033A0] p-3 shrink-0">
                                    <Mail className="h-6 w-6 text-[#40E0D0]" />
                                </span>
                                <div>
                                    <h2 className="font-heading font-bold text-xl text-slate-900">Email Us</h2>
                                    <a
                                        href={`mailto:${STORE.email}`}
                                        data-testid="contact-email"
                                        className="mt-2 inline-block text-lg font-bold text-[#0033A0] hover:underline underline-offset-4 break-all"
                                    >
                                        {STORE.email}
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Hours card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8" data-testid="contact-hours-card">
                            <div className="flex items-start gap-4">
                                <span className="rounded-full bg-[#0033A0] p-3 shrink-0">
                                    <Clock className="h-6 w-6 text-[#40E0D0]" />
                                </span>
                                <div className="flex-1">
                                    <h2 className="font-heading font-bold text-xl text-slate-900">Opening Hours</h2>
                                    <dl className="mt-3 space-y-2" data-testid="contact-hours">
                                        {STORE.hours.map((h) => (
                                            <div key={h.days} className="flex justify-between gap-4 text-slate-600 border-b border-slate-100 pb-2 last:border-0">
                                                <dt className="font-bold text-slate-900">{h.days}</dt>
                                                <dd>{h.time}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Map */}
                    <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200 min-h-[400px]" data-testid="contact-map-card">
                        <iframe
                            title="Map showing Freddie's Afro Caribbean Store, 33–35 Whitley Street, Reading"
                            src={STORE.mapEmbedUrl}
                            className="h-full w-full min-h-[400px]"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
}
